/* eslint-disable react-refresh/only-export-components */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { FileSearch, RefreshCw, Upload } from 'lucide-react';
import { ContentSection } from '@/app/(client)/audits/-components/digitized/ContentSection';
import { FindingsOverview } from '@/app/(client)/audits/-components/digitized/FindingsOverview';
import { SyncDigitizedAudit } from '@/app/(client)/audits/-components/digitized/SyncDigitizedAudit';
import { useAuditDetail } from '@/app/(client)/audits/-hooks/useAuditDetail';
import { useAuditDigitization } from '@/app/(client)/audits/-hooks/useAuditDigitization';
import { useSyncDigitizedAudit } from '@/app/(client)/audits/-hooks/useSyncDigitizedAudit';
import { FieldCard } from '@/components/shared/FieldLayout';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { useTranslator } from '@/hooks/useTranslator';

export const Route = createFileRoute(
  '/(client)/audits/(pages)/(detail)/$auditId/content/',
)({
  component: AuditContentPage,
});

function AuditContentPage() {
  const { auditId } = Route.useParams();
  const navigate = useNavigate();
  const { getTranslation: t } = useTranslator({ page: 'auditDetail' });
  const { data: audit } = useAuditDetail(auditId);
  const { data: digitized } = useAuditDigitization(auditId);

  const sync = useSyncDigitizedAudit({
    auditId,
    digitizedAuditId: digitized?.digitizedAuditId,
    extractedDatapoints: digitized?.extractedDatapoints,
    companyId: audit?.companyId,
  });

  const sections = digitized?.sections ?? [];
  const findings = digitized?.findings ?? [];

  const openFindings = () =>
    navigate({ to: '/audits/$auditId/findings', params: { auditId } });

  if (sections.length === 0) {
    return (
      <div className='p-4 @md:p-6'>
        <EmptyState
          icon={<FileSearch className='size-5' />}
          title={t('content.empty.title')}
          description={t('content.empty.description')}
        />
      </div>
    );
  }

  const showSyncBanner = sync.isReady && sync.availableCount > 0;

  return (
    <>
      {/* Five-column grid so the center column spans three columns — the width
          of three top stat cards — with a one-column rail on each side. */}
      <div className='grid grid-cols-1 @5xl:grid-cols-5 @5xl:items-start gap-4 @5xl:gap-3 p-4 @md:p-6'>
        {/* Left: findings overview as a content menu — always present (shows
            its own empty state) so both rails stay for layout consistency. */}
        <aside className='@5xl:col-start-1 @5xl:col-span-1 @5xl:sticky @5xl:top-16 @5xl:max-h-[calc(100dvh-7rem)] overflow-y-auto'>
          <FindingsOverview
            findings={findings}
            onOpenFindings={openFindings}
            t={t}
          />
        </aside>

        {/* Center: the collapsible content sections — the primary column. */}
        <div className='@5xl:col-start-2 @5xl:col-span-3 min-w-0 flex flex-col gap-3'>
          {sections.map((section, index) => (
            <ContentSection
              key={`${section.title}-${index}`}
              section={section}
              onOpenFindings={openFindings}
              t={t}
            />
          ))}
        </div>

        {/* Right: the sync-to-profile section — always present; shows the CTA
            when there are data points to sync, otherwise its empty state. */}
        <aside className='@5xl:col-start-5 @5xl:col-span-1 @5xl:sticky @5xl:top-16'>
          <FieldCard
            title={t('sync.bannerTitle')}
            icon={<RefreshCw />}
            {...(showSyncBanner
              ? {
                  description: t('sync.bannerDescription', {
                    count: sync.availableCount,
                  }),
                }
              : {})}
          >
            {showSyncBanner ? (
              <div className='px-6 pb-6'>
                <Button size='sm' onClick={sync.open}>
                  <Upload />
                  {t('sync.bannerButton')}
                </Button>
              </div>
            ) : (
              <EmptyState
                icon={<RefreshCw className='size-5' />}
                title={t('sync.empty')}
                className='py-8'
              />
            )}
          </FieldCard>
        </aside>
      </div>

      <SyncDigitizedAudit
        isOpen={sync.isOpen}
        onClose={sync.close}
        stage={sync.stage}
        existingAnswers={sync.existingAnswers}
        newAnswers={sync.newAnswers}
        syncedCount={sync.syncedCount}
        onConfirm={sync.prepare}
        onKeepExisting={() => sync.sync(sync.newAnswers)}
        onUpdateAll={() =>
          sync.sync([...sync.newAnswers, ...sync.existingAnswers])
        }
        t={t}
      />
    </>
  );
}
